import { useCallback, useEffect, useState } from "react";
import { LayoutGrid, Building2, PlusCircle, Inbox, IndianRupee, CalendarCheck, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import api, { formatINR, fmtErr, LEAD_STATUSES, LEAD_STATUS_LABELS, LEAD_STATUS_COLORS, LISTING_STATUS_COLORS } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout, { StatCard, EmptyState, Th, Td } from "./DashboardLayout";
import VisitCalendar from "./VisitCalendar";
import ListingForm from "../../components/ListingForm";

export const fmtSlot = (s) => {
  try {
    return new Date(s).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
  } catch {
    return s;
  }
};

export function VisitBadge({ lead }) {
  const sv = lead.site_visit;
  if (!sv || sv.status === "none") return null;
  if (sv.status === "confirmed" && sv.confirmed) {
    return (
      <span className="mt-1 inline-flex items-center gap-1 bg-cyan-100 px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-cyan-900" data-testid={`visit-confirmed-${lead.id}`}>
        <CalendarCheck size={10} /> Visit {fmtSlot(sv.confirmed.slot)}
      </span>
    );
  }
  if (sv.proposed?.length > 0) {
    return (
      <span className="mt-1 inline-block bg-neutral-100 px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-neutral-600" data-testid={`visit-proposed-${lead.id}`}>
        {sv.proposed.length} slot{sv.proposed.length > 1 ? "s" : ""} proposed
      </span>
    );
  }
  return null;
}

export function VisitProposer({ lead, onDone }) {
  const [slot, setSlot] = useState("");
  const propose = async () => {
    if (!slot) return toast.error("Pick a date & time first");
    try {
      await api.post(`/leads/${lead.id}/visits/propose`, { slot });
      toast.success("Visit slot proposed to the buyer");
      setSlot("");
      onDone();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };
  return (
    <div className="mt-2 flex items-center gap-1.5">
      <input
        type="datetime-local"
        className="dash-input w-40 px-2 py-1 text-[11px]"
        value={slot}
        onChange={(e) => setSlot(e.target.value)}
        data-testid={`visit-slot-input-${lead.id}`}
      />
      <button type="button" className="dash-btn-ghost" onClick={propose} data-testid={`visit-propose-${lead.id}`}>
        Propose
      </button>
    </div>
  );
}

export function RescheduleControl({ lead, onDone }) {
  const [slot, setSlot] = useState("");
  const sv = lead.site_visit || {};
  if (sv.status !== "confirmed") return null;
  const rs = sv.reschedule;

  const propose = async () => {
    if (!slot) return toast.error("Pick a date & time first");
    try {
      await api.post(`/leads/${lead.id}/visits/reschedule`, { slot });
      toast.success("New time proposed — the buyer has been notified");
      setSlot("");
      onDone();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const accept = async () => {
    try {
      await api.post(`/leads/${lead.id}/visits/confirm`, { slot: rs.slot });
      toast.success("New time confirmed");
      onDone();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  if (rs?.side === "buyer") {
    return (
      <div className="mt-1.5 flex items-center gap-2" data-testid={`reschedule-request-${lead.id}`}>
        <span className="text-[11px] text-black/60">Buyer asks: <strong>{fmtSlot(rs.slot)}</strong></span>
        <button type="button" className="dash-btn-ghost" onClick={accept} data-testid={`reschedule-accept-${lead.id}`}>Accept</button>
      </div>
    );
  }
  if (rs?.side === "host") {
    return <p className="mt-1.5 text-[11px] text-black/45" data-testid={`reschedule-waiting-${lead.id}`}>You proposed {fmtSlot(rs.slot)} — awaiting buyer</p>;
  }
  return (
    <div className="mt-1.5 flex items-center gap-1.5">
      <input
        type="datetime-local"
        className="dash-input w-40 px-2 py-1 text-[11px]"
        value={slot}
        onChange={(e) => setSlot(e.target.value)}
        data-testid={`reschedule-input-${lead.id}`}
      />
      <button type="button" className="dash-btn-ghost" onClick={propose} data-testid={`reschedule-propose-${lead.id}`}>Reschedule</button>
    </div>
  );
}

export default function VendorDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [listings, setListings] = useState([]);
  const [leads, setLeads] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [editing, setEditing] = useState(null);
  const [noteDrafts, setNoteDrafts] = useState({});

  const isPending = user?.status === "pending";
  const profileIncomplete = ["builder", "agent"].includes(user?.role) && (!user?.company || !user?.company_address);
  const loadStats = useCallback(() => api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {}), []);
  const loadListings = useCallback(() => api.get("/properties/my/listings").then(({ data }) => setListings(data.properties)).catch(() => {}), []);
  const loadLeads = useCallback(() => api.get("/leads").then(({ data }) => setLeads(data.leads)).catch(() => {}), []);
  const loadCommissions = useCallback(() => api.get("/commissions").then(({ data }) => setCommissions(data.commissions)).catch(() => {}), []);

  useEffect(() => { loadStats(); loadListings(); }, [loadStats, loadListings]);
  useEffect(() => {
    if (tab === "leads" || tab === "calendar") loadLeads();
    if (tab === "commissions") loadCommissions();
  }, [tab, loadLeads, loadCommissions]);

  const reloadLeads = () => { loadLeads(); loadStats(); };

  const updateLead = async (id, patch) => {
    try {
      await api.patch(`/leads/${id}`, patch);
      toast.success("Lead updated");
      reloadLeads();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  return (
    <DashboardLayout
      title={user?.role === "builder" ? "Developer Desk" : "Broker Desk"}
      activeTab={tab}
      onTabChange={setTab}
      tabs={[
        { key: "overview", label: "Overview", icon: LayoutGrid },
        { key: "listings", label: "My Listings", icon: Building2 },
        { key: "add", label: editing ? "Edit Listing" : "Add Listing", icon: PlusCircle },
        { key: "leads", label: "Leads", icon: Inbox, badge: stats?.leads?.new || 0 },
        { key: "calendar", label: "Visit Calendar", icon: CalendarDays },
        { key: "commissions", label: "Commissions", icon: IndianRupee },
      ]}
    >
      {isPending && (
        <div className="mb-6 border border-amber-300 bg-amber-50 p-5" data-testid="vendor-pending-banner">
          <p className="font-serif text-xl text-amber-900">Your partner application is under review.</p>
          <p className="mt-1 text-sm text-amber-800/80">Our curation desk verifies every partner. You'll be able to publish listings once approved.</p>
        </div>
      )}
      {!isPending && profileIncomplete && (
        <div className="mb-6 border border-amber-300 bg-amber-50 p-5" data-testid="profile-incomplete-banner">
          <p className="font-serif text-xl text-amber-900">Complete your office profile.</p>
          <p className="mt-1 text-sm text-amber-800/80">Office/company name and address are mandatory for partners — your listings can't go live without them. Open "Edit Profile" in the sidebar.</p>
        </div>
      )}

      {tab === "overview" && stats && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="vendor-overview">
          <StatCard testid="stat-live" label="Live Listings" value={stats.listings?.published || 0} sub={`${stats.listings?.pending_review || 0} in review`} />
          <StatCard testid="stat-views" label="Total Views" value={(stats.total_views || 0).toLocaleString("en-IN")} />
          <StatCard testid="stat-leads" label="Leads" value={Object.values(stats.leads || {}).reduce((a, b) => a + b, 0)} sub={`${stats.leads?.won || 0} won`} />
          <StatCard testid="stat-commission" label="Commission Earned" value={formatINR((stats.commissions?.approved || 0) + (stats.commissions?.paid || 0))} sub={`${formatINR(stats.commissions?.pending || 0)} pending`} />
        </div>
      )}

      {tab === "listings" && (
        <div className="dash-card overflow-x-auto" data-testid="vendor-listings">
          {listings.length === 0 ? <EmptyState title="No listings yet" hint="Add your first residence from the Add Listing tab." /> : (
            <table className="w-full min-w-[720px]">
              <thead><tr><Th>Listing</Th><Th>Price</Th><Th>Views</Th><Th>Status</Th><Th></Th></tr></thead>
              <tbody>
                {listings.map((p) => (
                  <tr key={p.id} data-testid={`listing-row-${p.id}`}>
                    <Td><p className="font-medium text-black">{p.title}</p><p className="text-[11px] text-black/45">{p.type} · {p.locality} · {p.beds} bed · {p.sqft} ft²</p>
                      {p.status === "rejected" && p.rejection_reason && <p className="mt-1 text-[11px] text-red-600">Rejected: {p.rejection_reason}</p>}
                    </Td>
                    <Td className="font-medium">{formatINR(p.price, p.price_unit)}</Td>
                    <Td>{p.views}</Td>
                    <Td><span className={`px-2 py-1 font-mono text-[9px] uppercase tracking-widest ${LISTING_STATUS_COLORS[p.status]}`}>{p.status.replace("_", " ")}</span></Td>
                    <Td><button className="dash-btn-ghost" data-testid={`edit-listing-${p.id}`} onClick={() => { setEditing(p); setTab("add"); }}>Edit</button></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "add" && (
        <ListingForm
          key={editing?.id || "new"}
          initial={editing}
          showProject={user?.role === "builder"}
          disabled={isPending}
          onDone={() => { setEditing(null); setTab("listings"); loadListings(); loadStats(); }}
          onCancel={editing ? () => { setEditing(null); setTab("listings"); } : null}
        />
      )}

      {tab === "leads" && (
        <div className="dash-card overflow-x-auto" data-testid="vendor-leads">
          {leads.length === 0 ? <EmptyState title="No leads yet" hint="Enquiries on your listings land here." /> : (
            <table className="w-full min-w-[860px]">
              <thead><tr><Th>Lead</Th><Th>Property</Th><Th>Status</Th><Th>Note & Visit</Th><Th></Th></tr></thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} data-testid={`lead-row-${l.id}`}>
                    <Td><p className="font-medium text-black">{l.name}</p><p className="text-[11px] text-black/45">{l.email}{l.phone ? ` · ${l.phone}` : ""}</p><p className="mt-1 max-w-[220px] text-[11px] italic text-black/50">"{l.message}"</p></Td>
                    <Td>{l.property_title}</Td>
                    <Td>
                      {l.assigned_to === user?.id ? (
                        <select className="dash-input w-36" value={l.status} data-testid={`lead-status-${l.id}`} onChange={(e) => updateLead(l.id, { status: e.target.value })}>
                          {LEAD_STATUSES.map((s) => <option key={s} value={s}>{LEAD_STATUS_LABELS[s]}</option>)}
                        </select>
                      ) : (
                        <span className={`px-2 py-1 font-mono text-[9px] uppercase tracking-widest ${LEAD_STATUS_COLORS[l.status]}`}>{LEAD_STATUS_LABELS[l.status]}</span>
                      )}
                      <p className="mt-1 text-[10px] text-black/40">{l.assigned_to_name ? `with ${l.assigned_to_name}` : "unassigned"}</p>
                      <br />
                      <VisitBadge lead={l} />
                    </Td>
                    <Td>
                      <input className="dash-input w-44" placeholder="Add a note…" value={noteDrafts[l.id] || ""} onChange={(e) => setNoteDrafts({ ...noteDrafts, [l.id]: e.target.value })} data-testid={`lead-note-${l.id}`} />
                      {l.notes?.length > 0 && <p className="mt-1 text-[10px] text-black/40">Last: {l.notes[l.notes.length - 1].text}</p>}
                      {l.assigned_to === user?.id && <VisitProposer lead={l} onDone={reloadLeads} />}
                      {l.assigned_to === user?.id && <RescheduleControl lead={l} onDone={reloadLeads} />}
                    </Td>
                    <Td><button className="dash-btn-ghost" data-testid={`lead-note-save-${l.id}`} onClick={() => { if (noteDrafts[l.id]?.trim()) { updateLead(l.id, { note: noteDrafts[l.id].trim() }); setNoteDrafts({ ...noteDrafts, [l.id]: "" }); } }}>Save</button></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "calendar" && <VisitCalendar leads={leads} />}

      {tab === "commissions" && (
        <div className="dash-card overflow-x-auto" data-testid="vendor-commissions">
          {commissions.length === 0 ? <EmptyState title="No commissions yet" hint="Closed deals logged by the house appear here." /> : (
            <table className="w-full min-w-[620px]">
              <thead><tr><Th>Deal</Th><Th>Value</Th><Th>Rate</Th><Th>Your Amount</Th><Th>Status</Th></tr></thead>
              <tbody>
                {commissions.map((c) => (
                  <tr key={c.id} data-testid={`commission-row-${c.id}`}>
                    <Td className="font-medium text-black">{c.property_title || "—"}</Td>
                    <Td>{formatINR(c.deal_value)}</Td>
                    <Td>{c.rate_percent}%</Td>
                    <Td className="font-medium text-black">{formatINR(c.amount)}</Td>
                    <Td><span className={`px-2 py-1 font-mono text-[9px] uppercase tracking-widest ${c.status === "paid" ? "bg-emerald-100 text-emerald-900" : c.status === "approved" ? "bg-amber-100 text-amber-900" : "bg-neutral-200 text-neutral-600"}`}>{c.status}</span></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </DashboardLayout>
  );
}
